import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { usePeriodDates } from '../../../../../../../../../hooks/usePeriodDates'
import { DownloadFormat } from '../../../../../../../enums/downloadFormat'
import { useSalesByCustomerReport } from '../../../../../../../hooks/salesByCustomerReport/useSalesByCustomerReport'
import { useExportSupportedLocale } from '../../../../../../../hooks/useExportSupportedLocale'
import { useSalesReportFilters } from '../../../../../../../hooks/useSalesReportFilters'
import { BasicExportActionDropdownItem } from '../../../../../../../types/exportActionDropdownItem'
import { ExportFunction } from '../../../../../../../types/exportFunction'
import { downloadSalesByCustomerReportFile } from '../utils/downloadSalesByCustomerReportFile'
import { useDownloadSalesByCustomerReportPdf } from './useDownloadSalesByCustomerReportPdf'

export const useSalesByCustomerReportExportActions = () => {
  const { t } = useTranslation()
  const locale = useExportSupportedLocale()
  const { organization } = useUserOrganization()
  const { firstFiscalYearStart } = organization || {}

  const { salesByCustomerReport } = useSalesByCustomerReport()
  const [{ period }] = useSalesReportFilters()
  const { fromDate: startDate, toDate: endDate } = usePeriodDates(period)

  const { download: downloadPdf, isDownloading: isDownloadingPdf } = useDownloadSalesByCustomerReportPdf()

  const isExportDisabled = !salesByCustomerReport.length

  const exportAsPdf = useCallback(() => {
    downloadPdf()
  }, [downloadPdf])

  const downloadAsXlsx = useCallback(() => {
    downloadSalesByCustomerReportFile({
      endDate,
      fileName: t('sales_report.sales_by_customer.export_file.title'),
      firstFiscalYearStart,
      format: DownloadFormat.xlsx,
      locale,
      startDate,
      t,
    })
  }, [endDate, firstFiscalYearStart, locale, startDate, t])

  const downloadAsCsv = useCallback(() => {
    downloadSalesByCustomerReportFile({
      endDate,
      fileName: t('sales_report.sales_by_customer.export_file.title'),
      firstFiscalYearStart,
      format: DownloadFormat.csv,
      locale,
      startDate,
      t,
    })
  }, [endDate, firstFiscalYearStart, locale, startDate, t])

  const performSalesReportExport: Record<BasicExportActionDropdownItem, ExportFunction> = useMemo(
    () => ({
      downloadAsCsv,
      downloadAsXlsx,
      exportAsPdf,
    }),
    [downloadAsCsv, downloadAsXlsx, exportAsPdf],
  )

  return {
    isExportDisabled,
    isExporting: isDownloadingPdf,
    performSalesReportExport,
  }
}
